import { UploadCloud } from "lucide-react";
import { useCallback } from "react";
import { type Accept, useDropzone } from "react-dropzone";

import { cn } from "@/lib/utils";

type Props = {
  onFile: (file: File) => void;
  disabled?: boolean;
  /** Override accepted MIME→ext map. Defaults to .pptx only. */
  accept?: Accept;
  /** Max file size in bytes. Defaults to 50 MB. */
  maxSizeBytes?: number;
  /** Headline shown when idle. Defaults to the .pptx prompt. */
  title?: string;
  /** Secondary hint line. */
  hint?: string;
};

const PPTX_ACCEPT: Accept = {
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
};

const DEFAULT_MAX_BYTES = 50 * 1024 * 1024;

export function DropZone({ onFile, disabled, accept, maxSizeBytes, title, hint }: Props) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted[0]) onFile(accepted[0]);
    },
    [onFile],
  );

  const maxSize = maxSizeBytes ?? DEFAULT_MAX_BYTES;

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: accept ?? PPTX_ACCEPT,
    maxFiles: 1,
    maxSize,
    disabled,
  });

  const rejection = fileRejections[0]?.errors[0]?.message;
  const mb = Math.round(maxSize / (1024 * 1024));

  return (
    <div>
      <div
        {...getRootProps()}
        className={cn(
          "cursor-pointer rounded-[14px] border-2 border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center transition sm:p-12",
          isDragActive && "border-[var(--color-coral)] bg-[var(--color-coral-soft)]/20",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <input {...getInputProps()} />
        <UploadCloud size={36} className="mx-auto mb-3 text-[var(--color-ink-muted)] sm:mb-4" />
        <p className="mb-2 font-display text-lg sm:text-xl">
          {isDragActive ? "ここにドロップ" : (title ?? ".pptx をドラッグ＆ドロップ")}
        </p>
        <p className="text-xs text-[var(--color-ink-muted)] sm:text-sm">
          {hint ?? `またはクリックで選択 · 最大 ${mb} MB`}
        </p>
      </div>
      {rejection && <p className="mt-3 text-sm text-[var(--color-coral-dark)]">{rejection}</p>}
    </div>
  );
}
